import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Card from '../../../../../shared/components/Card';
import userService, { UserWithRole, UserIpHistory } from '../../../../../shared/services/userService';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';
import LoadingSpinner from '../../../../../shared/components/LoadingSpinner';

interface UserDetailsModalProps {
  user: UserWithRole;
  onClose: () => void;
  onResetPassword?: (userId: number) => Promise<void>;
  resettingPassword?: boolean;
  onSanction?: (userId: number) => void;
  onForceLogout?: (userId: number) => Promise<void>;
  currentUserRoleId?: number;
  currentUserId?: number;
  onlineUserIds?: Set<number>;
  context?: 'roles' | 'users'; // 'roles' = gestion des rôles (CM uniquement), 'users' = gestion des utilisateurs (Modo, Admin, CM)
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  user,
  onClose,
  onResetPassword,
  resettingPassword = false,
  onSanction,
  onForceLogout,
  currentUserRoleId,
  currentUserId,
  onlineUserIds,
  context = 'users'
}) => {
  const { t } = usePageTranslation('panel', 'UsersManagement');
  const [ipHistory, setIpHistory] = useState<UserIpHistory[]>([]);
  const [loadingIpHistory, setLoadingIpHistory] = useState(true);
  const [clearAuthLoading, setClearAuthLoading] = useState(false);
  const [clearAuthMessage, setClearAuthMessage] = useState<string | null>(null);
  const [clearAuthIsError, setClearAuthIsError] = useState(false);

  // Permissions pour les sanctions
  const canSanction = currentUserRoleId && (
    context === 'roles' 
      ? currentUserRoleId === 1 // CM uniquement dans gestion des rôles
      : currentUserRoleId <= 3 // Modo, Admin, CM dans gestion des utilisateurs
  );

  useEffect(() => {
    const loadIpHistory = async () => {
      try {
        setLoadingIpHistory(true);
        const response = await userService.getUserIpHistory(user.id);
        if (response.success) {
          setIpHistory(response.data || []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'historique IP:', error);
        setIpHistory([]);
      } finally {
        setLoadingIpHistory(false);
      }
    };

    loadIpHistory();
  }, [user.id]);

  const getRoleBadgeColor = (roleId: number) => {
    switch (roleId) {
      case 1:
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 2:
        return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 3:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 4:
        return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
      case 5:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 6:
        return 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };

  const handleResetPassword = async () => {
    if (!onResetPassword) return;
    
    const confirmMessage = `${t('resetPassword.confirm')} ${user.username}?`;
    if (window.confirm(confirmMessage)) {
      await onResetPassword(user.id);
    }
  };

  const handleForceLogout = async () => {
    if (!onForceLogout) return;
    
    const confirmMessage = `Êtes-vous sûr de vouloir déconnecter de force ${user.username} ?`;
    if (window.confirm(confirmMessage)) {
      await onForceLogout(user.id);
    }
  };

  const canClearAuthRateLimit = Boolean(currentUserRoleId && currentUserRoleId <= 2);

  const handleClearAuthRateLimit = async () => {
    if (!canClearAuthRateLimit) return;
    const confirmMessage =
      t('authRateLimit.confirm') ||
      `Réinitialiser le blocage connexion/inscription (trop de tentatives) pour ${user.username} ?`;
    if (!window.confirm(confirmMessage)) return;
    setClearAuthLoading(true);
    setClearAuthMessage(null);
    setClearAuthIsError(false);
    try {
      const res = await userService.clearAuthRateLimit(user.id);
      if (res.success) {
        setClearAuthIsError(false);
        setClearAuthMessage(res.message || t('authRateLimit.success') || 'Blocage réinitialisé.');
      } else {
        setClearAuthIsError(true);
        setClearAuthMessage(res.error || t('authRateLimit.error') || 'Erreur.');
      }
    } catch (e: any) {
      setClearAuthIsError(true);
      setClearAuthMessage(e?.message || t('authRateLimit.error') || 'Erreur.');
    } finally {
      setClearAuthLoading(false);
    }
  };

  const isOnline = onlineUserIds && onlineUserIds.has(user.id);
  const canForceLogout = onForceLogout && isOnline && user.id !== currentUserId;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 pt-24">
      <Card className="max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white">
            {t('details.title')} - {user.username}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          {/* Informations de base */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-4">{t('details.basicInfo')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400">{t('details.id')}</label>
                <p className="text-white font-medium">#{user.id}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">{t('details.username')}</label>
                <p className="text-white font-medium">{user.username}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">{t('details.email')}</label>
                <p className="text-white font-medium">{user.email}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">{t('details.role')}</label>
                <div className="mt-1">
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getRoleBadgeColor(user.roles_id)}`}>
                    {user.role_name || `Rôle ${user.roles_id}`}
                  </span>
                  {user.role_description && (
                    <p className="text-gray-400 text-xs mt-1">{user.role_description}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400">{t('details.status')}</label>
                <div className="mt-1">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    user.activated
                      ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                      : 'bg-red-500/20 text-red-400 border border-red-500/50'
                  }`}>
                    {user.activated ? t('status.active') : t('status.inactive')}
                  </span>
                </div>
              </div>
              {user.last_username_change && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.lastUsernameChange')}</label>
                  <p className="text-white font-medium">
                    {new Date(user.last_username_change).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Informations de compte */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-4">{t('details.accountInfo')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400">{t('details.accountCreated')}</label>
                <p className="text-white font-medium">
                  {user.created_at ? new Date(user.created_at).toLocaleString('fr-FR') : '-'}
                </p>
              </div>
              {user.updated_at && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.lastUpdated')}</label>
                  <p className="text-white font-medium">
                    {new Date(user.updated_at).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-400">{t('details.lastLogin')}</label>
                <p className="text-white font-medium">
                  {user.last_login ? new Date(user.last_login).toLocaleString('fr-FR') : t('details.never')}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">{t('details.lastActivity')}</label>
                <p className="text-white font-medium">
                  {user.last_activity ? new Date(user.last_activity).toLocaleString('fr-FR') : t('details.never')}
                </p>
              </div>
            </div>
          </div>

          {/* Informations Premium */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-4">{t('details.premiumInfo')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400">{t('details.premium')}</label>
                <p className="text-white font-medium">
                  {user.premium ? (
                    <span className="text-yellow-400">{t('details.yes')}</span>
                  ) : (
                    <span className="text-gray-400">{t('details.no')}</span>
                  )}
                </p>
              </div>
              {user.premium_type !== undefined && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.premiumType')}</label>
                  <p className="text-white font-medium">{user.premium_type || '-'}</p>
                </div>
              )}
              {user.premium_start && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.premiumStart')}</label>
                  <p className="text-white font-medium">
                    {new Date(user.premium_start).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
              {user.premium_end && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.premiumEnd')}</label>
                  <p className="text-white font-medium">
                    {new Date(user.premium_end).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
              {user.premium_until && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.until')}</label>
                  <p className="text-white font-medium">
                    {new Date(user.premium_until).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Informations de jeu */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-4">{t('details.gameInfo')}</h4>
            <div className="grid grid-cols-2 gap-4">
              {user.xp !== undefined && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.xp')}</label>
                  <p className="text-white font-medium">{user.xp?.toLocaleString('fr-FR') || 0}</p>
                </div>
              )}
              {user.niveau !== undefined && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.level')}</label>
                  <p className="text-white font-medium">{user.niveau || 1}</p>
                </div>
              )}
              {user.departement_code && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.department')}</label>
                  <p className="text-white font-medium">
                    {user.department || user.departement_code}
                  </p>
                </div>
              )}
              {user.argents !== undefined && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.money')}</label>
                  <p className="text-white font-medium">{user.argents?.toLocaleString('fr-FR') || 0}</p>
                </div>
              )}
              {user.medailles !== undefined && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.medals')}</label>
                  <p className="text-white font-medium">{user.medailles?.toLocaleString('fr-FR') || 0}</p>
                </div>
              )}
              {user.batiments !== undefined && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.buildings')}</label>
                  <p className="text-white font-medium">{user.batiments || 0}</p>
                </div>
              )}
              {user.vehicules !== undefined && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.vehicles')}</label>
                  <p className="text-white font-medium">{user.vehicules || 0}</p>
                </div>
              )}
            </div>
            {user.statistiques && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <h5 className="text-white font-medium mb-3">{t('details.statistics')}</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">{t('details.statBuildings')}</label>
                    <p className="text-white font-medium">{user.statistiques.batiments || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">{t('details.statVehicles')}</label>
                    <p className="text-white font-medium">{user.statistiques.vehicules || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">{t('details.statInterventions')}</label>
                    <p className="text-white font-medium">{user.statistiques.interventions || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">{t('details.statTickets')}</label>
                    <p className="text-white font-medium">{user.statistiques.tickets_support || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">{t('details.statFriends')}</label>
                    <p className="text-white font-medium">{user.statistiques.amis || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">{t('details.statPersonnel')}</label>
                    <p className="text-white font-medium">{user.statistiques.personnel || 0}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Informations de sécurité */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-4">{t('details.securityInfo')}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400">{t('details.currentIp')}</label>
                <p className="text-white font-medium">
                  {user.last_ip || (
                    <span className="text-gray-500">{t('details.notAvailable')}</span>
                  )}
                </p>
              </div>
              {user.failed_attempts !== undefined && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.failedAttempts')}</label>
                  <p className="text-white font-medium">{user.failed_attempts || 0}</p>
                </div>
              )}
              {user.last_failed_attempt && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.lastFailedAttempt')}</label>
                  <p className="text-white font-medium">
                    {new Date(user.last_failed_attempt).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}
              {user.bann !== undefined && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.banStatus')}</label>
                  <p className="text-white font-medium">
                    {user.bann === 0 ? (
                      <span className="text-green-400">{t('details.notBanned')}</span>
                    ) : (
                      <span className="text-red-400">{t('details.banned')}</span>
                    )}
                  </p>
                </div>
              )}
              {user.ban_end && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.banEnd')}</label>
                  <p className="text-white font-medium">
                    {new Date(user.ban_end).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}
              {user.ban_reason && (
                <div className="col-span-2">
                  <label className="text-sm text-gray-400">{t('details.banReason')}</label>
                  <p className="text-white font-medium">{user.ban_reason}</p>
                </div>
              )}
              {user.muted && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.muted')}</label>
                  <p className="text-white font-medium">
                    {user.muted ? (
                      <span className="text-red-400">{t('details.yes')}</span>
                    ) : (
                      <span className="text-green-400">{t('details.no')}</span>
                    )}
                  </p>
                </div>
              )}
              {user.mute_end && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.muteEnd')}</label>
                  <p className="text-white font-medium">
                    {new Date(user.mute_end).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}
              {user.mute_reason && (
                <div className="col-span-2">
                  <label className="text-sm text-gray-400">{t('details.muteReason')}</label>
                  <p className="text-white font-medium">{user.mute_reason}</p>
                </div>
              )}
              {user.banned_ips_id && (
                <div>
                  <label className="text-sm text-gray-400">{t('details.bannedIpId')}</label>
                  <p className="text-white font-medium">#{user.banned_ips_id}</p>
                </div>
              )}
            </div>

            {/* Historique des IPs */}
            <div className="mt-6 pt-4 border-t border-gray-700">
              <h5 className="text-white font-medium mb-3">{t('details.ipHistory')}</h5>
              {loadingIpHistory ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner />
                </div>
              ) : ipHistory.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {ipHistory.map((ipRecord) => (
                    <div
                      key={ipRecord.id}
                      className={`p-3 rounded-lg border ${
                        ipRecord.ip_address === user.last_ip
                          ? 'bg-blue-500/10 border-blue-500/50'
                          : 'bg-gray-700/30 border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-white font-medium">
                            {ipRecord.ip_address}
                            {ipRecord.ip_address === user.last_ip && (
                              <span className="ml-2 px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/50">
                                {t('details.current')}
                              </span>
                            )}
                          </p>
                          <div className="flex gap-4 mt-1 text-xs text-gray-400">
                            <span>
                              {t('details.firstSeen')}: {new Date(ipRecord.first_seen).toLocaleString('fr-FR')}
                            </span>
                            <span>
                              {t('details.lastSeen')}: {new Date(ipRecord.last_seen).toLocaleString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">{t('details.noIpHistory')}</p>
              )}
            </div>
          </div>

          {/* Préférences */}
          {(user.email_notifications !== undefined || user.push_notifications !== undefined || user.newsletter !== undefined || user.profile_visibility || user.show_email !== undefined || user.show_stats !== undefined) && (
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h4 className="text-white font-semibold mb-4">{t('details.preferences')}</h4>
              <div className="grid grid-cols-2 gap-4">
                {user.email_notifications !== undefined && (
                  <div>
                    <label className="text-sm text-gray-400">{t('details.emailNotifications')}</label>
                    <p className="text-white font-medium">
                      {user.email_notifications ? (
                        <span className="text-green-400">{t('details.enabled')}</span>
                      ) : (
                        <span className="text-gray-400">{t('details.disabled')}</span>
                      )}
                    </p>
                  </div>
                )}
                {user.push_notifications !== undefined && (
                  <div>
                    <label className="text-sm text-gray-400">{t('details.pushNotifications')}</label>
                    <p className="text-white font-medium">
                      {user.push_notifications ? (
                        <span className="text-green-400">{t('details.enabled')}</span>
                      ) : (
                        <span className="text-gray-400">{t('details.disabled')}</span>
                      )}
                    </p>
                  </div>
                )}
                {user.newsletter !== undefined && (
                  <div>
                    <label className="text-sm text-gray-400">{t('details.newsletter')}</label>
                    <p className="text-white font-medium">
                      {user.newsletter ? (
                        <span className="text-green-400">{t('details.yes')}</span>
                      ) : (
                        <span className="text-gray-400">{t('details.no')}</span>
                      )}
                    </p>
                  </div>
                )}
                {user.profile_visibility && (
                  <div>
                    <label className="text-sm text-gray-400">{t('details.profileVisibility')}</label>
                    <p className="text-white font-medium">{user.profile_visibility}</p>
                  </div>
                )}
                {user.show_email !== undefined && (
                  <div>
                    <label className="text-sm text-gray-400">{t('details.showEmail')}</label>
                    <p className="text-white font-medium">
                      {user.show_email ? (
                        <span className="text-green-400">{t('details.yes')}</span>
                      ) : (
                        <span className="text-gray-400">{t('details.no')}</span>
                      )}
                    </p>
                  </div>
                )}
                {user.show_stats !== undefined && (
                  <div>
                    <label className="text-sm text-gray-400">{t('details.showStats')}</label>
                    <p className="text-white font-medium">
                      {user.show_stats ? (
                        <span className="text-green-400">{t('details.yes')}</span>
                      ) : (
                        <span className="text-gray-400">{t('details.no')}</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-4">{t('details.actions')}</h4>
            <div className="flex flex-wrap gap-3">
              {onResetPassword && (
                <button
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resettingPassword ? t('resetPassword.sending') : t('resetPassword.send')}
                </button>
              )}
              {canClearAuthRateLimit && (
                <button
                  type="button"
                  onClick={handleClearAuthRateLimit}
                  disabled={clearAuthLoading}
                  className="px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {clearAuthLoading
                    ? t('authRateLimit.loading') || 'Réinitialisation…'
                    : t('authRateLimit.button') || 'Débloquer tentatives connexion'}
                </button>
              )}
              {canForceLogout && (
                <button
                  onClick={handleForceLogout}
                  className="px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {t('actions.forceLogout') || 'Déconnecter de force'}
                </button>
              )}
              {onSanction && canSanction && (
                <button
                  onClick={() => onSanction(user.id)}
                  className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                >
                  {t('details.sanction')}
                </button>
              )}
            </div>
            {onResetPassword && (
              <p className="text-gray-400 text-sm mt-2">
                {t('resetPassword.description')}
              </p>
            )}
            {clearAuthMessage && (
              <p className={`text-sm mt-3 ${clearAuthIsError ? 'text-red-300' : 'text-green-300'}`}>{clearAuthMessage}</p>
            )}
          </div>
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
        >
          {t('details.close')}
        </button>
      </Card>
    </div>,
    document.body
  );
};

export default UserDetailsModal;

